import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/app/store';
import { ToastHost } from '@/components/ui/Toast';
import { PlanForm } from '@/features/plans/PlanForm';
import { DayPanel } from '@/features/plans/DayPanel';
import { OnboardingFlow } from '@/features/onboarding/OnboardingFlow';
import { useLayoutMode } from '@/platform/useLayoutMode';
import { format } from 'date-fns';
import { getDateFnsLocale, parseDateKey } from '@/domain/dates';
import { useTranslation } from 'react-i18next';
import { AppBackground } from './AppBackground';
import { BottomNav } from './BottomNav';
import { RightDrawer } from './RightDrawer';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { AppRoutes } from '@/pages/app/AppRoutes';
import styles from './AppShell.module.css';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const layoutMode = useLayoutMode();
  const location = useLocation();
  const navigate = useNavigate();

  const planEditorOpen = useAppStore((s) => s.planEditorOpen);
  const closePlanEditor = useAppStore((s) => s.closePlanEditor);
  const openPlanEditor = useAppStore((s) => s.openPlanEditor);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const rightPanelOpen = useAppStore((s) => s.rightPanelOpen);
  const setRightPanelOpen = useAppStore((s) => s.setRightPanelOpen);
  const setSelectedDate = useAppStore((s) => s.setSelectedDate);
  const setMobileTab = useAppStore((s) => s.setMobileTab);

  const isMobile = layoutMode === 'mobile';
  const isCompactSidebar = layoutMode === 'tablet' || layoutMode === 'desktop-compact';
  const [dayPanelStatic, setDayPanelStatic] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1180px)').matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1180px)');
    const onChange = () => setDayPanelStatic(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const showRightStatic = !isMobile && dayPanelStatic && rightPanelOpen;
  const showRightOverlay = rightPanelOpen && (isMobile || !dayPanelStatic);

  const dayPanelTitle = selectedDate
    ? format(parseDateKey(selectedDate), 'EEEE, MMM d', {
        locale: getDateFnsLocale(i18n.language.startsWith('ru') ? 'ru' : 'en'),
      })
    : t('app.selectDay');

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (planEditorOpen) {
          closePlanEditor();
          return;
        }
        if (rightPanelOpen) {
          setRightPanelOpen(false);
          return;
        }
      }

      if (isTypingTarget(event.target)) return;

      const mod = event.ctrlKey || event.metaKey;

      // Use event.code so shortcuts work on RU/EN (and other) keyboard layouts.
      if (mod && event.code === 'KeyN') {
        event.preventDefault();
        openPlanEditor();
        return;
      }
      if (mod && event.code === 'KeyK') {
        event.preventDefault();
        setMobileTab('search');
        navigate('/app/search');
        return;
      }
      if (mod && event.code === 'Comma') {
        event.preventDefault();
        setMobileTab('settings');
        navigate('/app/settings');
        return;
      }
      if (!mod && event.code === 'KeyT') {
        event.preventDefault();
        setMobileTab('today');
        navigate('/app/today');
      }
    },
    [
      closePlanEditor,
      navigate,
      openPlanEditor,
      planEditorOpen,
      rightPanelOpen,
      setMobileTab,
      setRightPanelOpen,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (location.pathname.includes('/settings')) {
      setRightPanelOpen(false);
    }
  }, [location.pathname, setRightPanelOpen]);

  return (
    <>
      <AppBackground />
      <div className={clsx('app-shell', styles.shell, isMobile && styles.mobile)}>
        <TopBar />

        <div className={styles.body}>
          {!isMobile && <Sidebar compact={isCompactSidebar} />}

          <main className={styles.main} id="main-content">
            <AppRoutes />
          </main>

          {showRightStatic && (
            <RightDrawer
              open
              onClose={() => {
                setRightPanelOpen(false);
                setSelectedDate(null);
              }}
              title={dayPanelTitle}
            >
              <DayPanel />
            </RightDrawer>
          )}
        </div>

        {isMobile && <BottomNav />}
      </div>

      {showRightOverlay && (
        <RightDrawer
          open
          overlay
          onClose={() => {
            setRightPanelOpen(false);
            setSelectedDate(null);
          }}
          title={dayPanelTitle}
        >
          <DayPanel />
        </RightDrawer>
      )}

      {planEditorOpen && <PlanForm />}
      <OnboardingFlow />
      <ToastHost />
    </>
  );
}
